import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { submitPasswordReset, validateResetToken } from '@/features/auth/api'
import { ApiError } from '@/api/client'
import logo from '@/assets/vision_controller_logo.jpg'

export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>()
  const { isAuthenticated, refreshSession } = useAuth()
  const navigate = useNavigate()

  const [checking, setChecking] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [tokenMessage, setTokenMessage] = useState<string | null>(null)

  const [password1, setPassword1] = useState('')
  const [password2, setPassword2] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    validateResetToken(token)
      .then((res) => {
        if (cancelled) return
        setTokenValid(res.valid)
        setTokenMessage(res.message ?? null)
      })
      .catch(() => {
        if (cancelled) return
        setTokenValid(false)
        setTokenMessage('Invalid or expired password reset link. Please request a new one.')
      })
      .finally(() => {
        if (!cancelled) setChecking(false)
      })
    return () => {
      cancelled = true
    }
  }, [token])

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!token) return
    setError(null)
    setSubmitting(true)
    try {
      const res = await submitPasswordReset(token, password1, password2)
      setSuccess(res.message)
      await refreshSession()
      setTimeout(() => navigate('/', { replace: true }), 1200)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'An error occurred. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-secondary px-4">
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 shadow-sm">
        <div className="mb-1 flex items-center gap-2">
          <img src={logo} alt="Vision Portal" className="h-8 w-8 object-contain" />
          <h1 className="text-2xl font-bold text-brand">Vision Portal</h1>
        </div>
        <p className="mb-6 text-sm text-text-secondary">Reset your password</p>

        {checking && <p className="text-sm text-text-secondary">Checking your reset link…</p>}

        {!checking && !tokenValid && (
          <div className="space-y-4">
            <p className="text-sm italic text-error">
              {tokenMessage ?? 'Invalid or expired password reset link. Please request a new one.'}
            </p>
            <Link to="/login" className="text-sm text-brand hover:underline">
              Back to sign in
            </Link>
          </div>
        )}

        {!checking && tokenValid && !success && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password1" className="mb-1 block text-sm text-text-primary">
                New password
              </label>
              <input
                id="password1"
                type="password"
                required
                minLength={10}
                autoComplete="new-password"
                value={password1}
                onChange={(e) => setPassword1(e.target.value)}
                className="w-full rounded border-2 border-brand bg-input px-3 py-2 text-text-primary outline-none"
              />
              <p className="mt-1 text-xs text-text-secondary">At least 10 characters.</p>
            </div>
            <div>
              <label htmlFor="password2" className="mb-1 block text-sm text-text-primary">
                Re-enter password
              </label>
              <input
                id="password2"
                type="password"
                required
                minLength={10}
                autoComplete="new-password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                className="w-full rounded border-2 border-brand bg-input px-3 py-2 text-text-primary outline-none"
              />
            </div>

            {error && <p className="text-sm italic text-error">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded bg-brand py-2 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
        )}

        {success && <p className="text-sm text-brand">{success} Redirecting…</p>}
      </div>
    </div>
  )
}
